import { Chip, Stack, StackProps, styled, Tooltip } from '@mui/material';
import { useCallback } from 'react';
import { TreeViewNodeWithParentData } from '.';
import { ExtendedTypography } from '../extended-typography';

type TreeLeavesProps = StackProps & {
  'data-testid'?: string;
};

type Props = {
  selectedLeaves: TreeViewNodeWithParentData[];
  onDelete: (id: string) => void;
  readOnly?: boolean;
  treeLeavesProps?: TreeLeavesProps;
};

const StyledChip = styled(Chip)`
  max-width: 250px;

  & .MuiChip-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export function SelectedTreeLeaves({
  selectedLeaves,
  onDelete,
  readOnly,
  treeLeavesProps,
}: Props) {
  const getLabel = useCallback(
    (leaf: TreeViewNodeWithParentData) => (
      <ExtendedTypography variant='textSm'>
        {leaf.parent?.name ? (
          <>
            <strong>{leaf.parent.name}</strong>
            {' - '}
            {leaf.name}
          </>
        ) : (
          <strong>{leaf.name}</strong>
        )}
      </ExtendedTypography>
    ),
    [],
  );

  const getTooltipText = useCallback(
    (leaf: TreeViewNodeWithParentData) =>
      leaf.parent?.name ? `${leaf.parent.name} - ${leaf.name}` : leaf.name,
    [],
  );

  if (selectedLeaves.length === 0) {
    return null;
  }

  return (
    <Stack direction='row' flexWrap='wrap' gap={1} {...treeLeavesProps}>
      {selectedLeaves.map((item) => (
        <Tooltip key={item.id} title={getTooltipText(item)} arrow>
          <StyledChip
            label={getLabel(item)}
            onDelete={readOnly ? undefined : () => onDelete(item.id)}
          />
        </Tooltip>
      ))}
    </Stack>
  );
}
